import React, { useEffect, useState, useRef } from 'react';
import { useParams, Link, useLocation } from 'react-router-dom';
import { XCircle, ShieldCheck, Download } from 'lucide-react';
import { QRCodeCanvas } from 'qrcode.react';
import QRCode from 'qrcode';
import { Certificate, CertificateStatus } from '../../types';
import { jsPDF } from 'jspdf';
import html2canvas from 'html2canvas';
import { toast } from 'react-toastify';
import { verifyCertificate, verifyCertificateQR } from '../../services/studentService';
import { fetchCertificates } from '../../services/dashboardService';
import { POS } from '../../services/positions';


export const VerificationResult: React.FC = () => {
  // Use a wildcard parameter to capture the entire path after /verify/
  const { '*' : pathParam } = useParams<{ '*': string }>();
  // The id is the path parameter, which contains the full certificate ID with slashes
  const id = pathParam || '';
  const routerLocation = useLocation();
  const [cert, setCert] = useState<Certificate | null>(null);
  const [loading, setLoading] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const certificateRef = useRef<HTMLDivElement>(null);
  const [inputCode, setInputCode] = useState('');
  const [attempted, setAttempted] = useState(false);
  const CERT_BG_URL = '/verify/certificate-bg.jpg';

  const buildQrPayload = (c: Certificate) => {
    // Get the certificate ID and ensure it has slashes (not hyphens)
    const certificate_number = String(c.certificateId || '').trim();
    // Use the frontend verification URL with the certificate ID
    const verify_url = `${location.href}`;
    return verify_url;
  };

  // Add this function at the top of your file, outside the component
const formatDate = (dateString: string): string => {
  if (!dateString) return '';

  const date = new Date(dateString);
  if (isNaN(date.getTime())) return dateString; // Return original if invalid date

  const day = date.getDate();
  const month = date.toLocaleString('default', { month: 'long' });
  const year = date.getFullYear();

  // Add the appropriate ordinal suffix
  const getOrdinalSuffix = (d: number) => {
    if (d > 3 && d < 21) return 'th';
    switch (d % 10) {
      case 1:  return 'st';
      case 2:  return 'nd';
      case 3:  return 'rd';
      default: return 'th';
    }
  };

  return `${day}${getOrdinalSuffix(day)} of ${month} ${year}`;
};

  const downloadCertificate = async () => {
    if (!certificateRef.current) return;
    
    setDownloading(true);
    try {
      // Load the certificate background image
      const loadImage = (url: string): Promise<HTMLImageElement> => {
        return new Promise((resolve, reject) => {
          const img = new Image();
          img.crossOrigin = 'anonymous';
          img.onload = () => resolve(img);
          img.onerror = reject;
          img.src = url.startsWith('http') ? url : `${window.location.origin}${url}`;
        });
      };

      const bgImage = await loadImage(CERT_BG_URL);
      
      // Create a clone of the certificate node
      const node = certificateRef.current.cloneNode(true) as HTMLElement;
      
      // Wait for all images to load
      const images = node.getElementsByTagName('img');
      await Promise.all(Array.from(images).map(img => {
        if (img.complete) return Promise.resolve();
        return new Promise((resolve, reject) => {
          img.onload = resolve;
          img.onerror = reject;
        });
      }));

      // Set the background image for the clone
      node.style.backgroundImage = `url(${bgImage.src})`;
      node.style.backgroundSize = 'cover';
      node.style.backgroundPosition = 'center';
      node.style.backgroundRepeat = 'no-repeat';
      node.style.width = '210mm'; // A4 width
      node.style.height = '297mm'; // A4 height
      node.style.padding = '0';
      node.style.margin = '0';

      // Generate PDF
      const canvas = await html2canvas(node, {
        scale: 2,
        useCORS: true,
        logging: false,
        allowTaint: true,
        backgroundColor: null
      });

      const imgData = canvas.toDataURL('image/png', 1.0);
      const pdf = new jsPDF('p', 'mm', 'a4');
      const imgProps = pdf.getImageProperties(imgData);
      const pdfWidth = pdf.internal.pageSize.getWidth();
      const pdfHeight = (imgProps.height * pdfWidth) / imgProps.width;

      pdf.addImage(imgData, 'PNG', 0, 0, pdfWidth, pdfHeight);
      pdf.save(`certificate-${cert?.certificateId || 'download'}.pdf`);
    } catch (error) {
      console.error('Error generating PDF:', error);
      toast.error('Failed to generate PDF. Please try again.');
    } finally {
      setDownloading(false);
    }
  };

  const getFitSize = (t: string, base: number, min: number, max: number) => {
    const W = 606;
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    let s = base;
    if (!ctx) return Math.min(Math.max(s, min), max);
    while (s > min) {
      ctx.font = `${s}px 'Times New Roman', Times, serif`;
      if (ctx.measureText(t || '').width <= W) break;
      s -= 1;
    }
    if (s < min) s = min;
    if (s > max) s = max;
    return s;
  };

  const getFitSizeNarrow = (t: string, base: number, min: number, max: number) => {
    const W = 260;
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    let s = base;
    if (!ctx) return Math.min(Math.max(s, min), max);
    while (s > min) {
      ctx.font = `${s}px 'Courier New', monospace`;
      if (ctx.measureText(t || '').width <= W) break;
      s -= 1;
    }
    if (s < min) s = min;
    if (s > max) s = max;
    return s;
  };

useEffect(() => {
  console.log("Router location changed:", { pathname: routerLocation.pathname, search: routerLocation.search });
  console.log("Current ID from params:", id);
  
  const qs = new URLSearchParams(routerLocation.search);
  const fromQuery = qs.get('id');
  const raw = fromQuery || id || '';
  console.log("Raw certificate ID from URL:", raw);
  
  if (raw) {
    // Convert hyphens to slashes for the certificate ID
    const certificateId = String(raw).replace(/-/g, '/');
    console.log("Processing certificate ID:", certificateId);
    setInputCode(certificateId);
    
    if (!attempted) {
      const mode = fromQuery ? 'qr' : 'manual';
      console.log(`Starting ${mode} verification for:`, certificateId);
      fetchCertificate(certificateId, mode);
    }
  }
}, [id, routerLocation.search]);

  const fetchCertificate = async (code: string, mode: 'manual' | 'qr' = 'manual') => {
    setAttempted(true);
    setLoading(true);
    try {
      const serviceFn = mode === 'qr' ? verifyCertificateQR : verifyCertificate;
      const data = await serviceFn(code);
      if (data.certificate) {
        setCert({
          id: '1',
          certificateId: data.certificate.verification_code,
          studentName: data.certificate.student_name,
          courseName: data.certificate.course_name,
          issueDate: data.certificate.issued_at || new Date().toISOString().split('T')[0],
          status: String(data.status || '').toUpperCase() === 'VALID' ? CertificateStatus.VALID : CertificateStatus.INVALID,
          description: `For successfully completing the course ${data.certificate.course_name}`,
          verifyUrl: data.certificate.verification_url || data.certificate.verify_url || undefined,
          qrImageUrl: data.certificate.qr_url || data.certificate.qr_code_url || undefined,
        });
      } else {
        const up = code.toUpperCase();
        if (up !== code) {
          const d2 = await serviceFn(up);
          if (d2.certificate) {
            setCert({
              id: '1',
              certificateId: d2.certificate.verification_code,
              studentName: d2.certificate.student_name,
              courseName: d2.certificate.course_name,
              issueDate: d2.certificate.issued_at || new Date().toISOString().split('T')[0],
              status: String(d2.status || '').toUpperCase() === 'VALID' ? CertificateStatus.VALID : CertificateStatus.INVALID,
              description: `For successfully completing the course ${d2.certificate.course_name}`,
              verifyUrl: d2.certificate.verification_url || d2.certificate.verify_url || undefined,
              qrImageUrl: d2.certificate.qr_url || d2.certificate.qr_code_url || undefined,
            });
            return;
          }
        }
        try {
          const list = await fetchCertificates();
          const target = [code, code.toUpperCase()];
          const arr = Array.isArray(list?.certificates) ? list.certificates : Array.isArray(list) ? list : [];
          const found = arr.find((c: any) => target.includes(String(c.verification_code || c.code || '').trim()));
          if (found) {
            setCert({
              id: String(found.id || ''),
              certificateId: found.verification_code,
              studentName: found.student_name,
              courseName: found.course_name,
              issueDate: found.issued_at || new Date().toISOString().split('T')[0],
              status: String(found.status || found.verification_status || 'INVALID') as CertificateStatus,
              description: `For successfully completing the course ${found.course_name}`,
              verifyUrl: found.verification_url || found.verify_url || undefined,
              qrImageUrl: found.qr_url || found.qr_code_url || undefined,
            });
          } else {
            setCert(null);
          }
        } catch {
          setCert(null);
        }
      }
    } catch (err) {
      console.error(err);
      setCert(null);
    } finally {
      setLoading(false);
    }
  };


// removed duplicate fetch/useEffect
const verifyUrl = `${location.href}`;

  const handleVerifyClick = () => {
  if (!inputCode) {
    alert('Please enter a certificate ID');
    return;
  }
  // Convert hyphens to slashes for the certificate ID
  const certificateId = inputCode.replace(/-/g, '/');
  console.log("Verifying certificate ID:", certificateId);
  fetchCertificate(certificateId, 'manual');
};

  const loadImageAsDataUrl = (url: string, mime: string = 'image/png') =>
  new Promise<string>((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => {
      try {
        const c = document.createElement('canvas');
        c.width = img.naturalWidth;
        c.height = img.naturalHeight;
        const ctx = c.getContext('2d');
        if (!ctx) return reject(new Error('Canvas context unavailable'));
        ctx.drawImage(img, 0, 0);
        const data = c.toDataURL(mime);
        resolve(data);
      } catch (err) {
        reject(err);
      }
    };
    img.onerror = (e) => reject(e);
    img.src = url;
  });

   const handleDownloadPDF = async () => {
    if (!cert) return;
    setDownloading(true);
    try {
      let bg: string;
      try {
        bg = await loadImageAsDataUrl(CERT_BG_URL, 'image/jpeg');
      } catch (e) {
        console.error('Background loader failed, trying fetch fallback', e);
        const r = await fetch(CERT_BG_URL);
        const b = await r.blob();
        bg = await new Promise<string>((resolve) => { const fr = new FileReader(); fr.onloadend = () => resolve(fr.result as string); fr.readAsDataURL(b); });
      }
      const dims = await new Promise<{ w: number; h: number }>((resolve, reject) => { const i = new Image(); i.onload = () => resolve({ w: i.naturalWidth, h: i.naturalHeight }); i.onerror = (e) => reject(e); i.src = bg; });
      const w = dims.w, h = dims.h;
      const k = Math.min(w / 842, h / 595);
      const px = (n: number) => Math.round(n * k);
      const cx = (n: number) => Math.round((n * w) / 842);
      const cy = (n: number) => Math.round((n * h) / 595);
      const pdf = new jsPDF({ orientation: 'landscape', unit: 'px', format: [w, h] });
      pdf.addImage(bg, 'JPEG', 0, 0, w, h, undefined, 'NONE');
      pdf.setFont('times', 'bold');
      const maxW = cx(606);
      pdf.setFontSize(px(56));
      pdf.setTextColor('#111827');
      pdf.text(cert.studentName, Math.round(w / 2), cy(267), { align: 'center' });
      pdf.setFont('times', 'normal');
      let sCourse = px(30);
      pdf.setFontSize(sCourse);
      pdf.setTextColor('#1e3a8a');
      const courseText = (cert.courseName || '').toUpperCase();
      let courseLines = pdf.splitTextToSize(courseText, maxW);
      if (courseLines.length > 2) courseLines = courseLines.slice(0, 2);
      if (courseLines.length > 1) { sCourse = px(26); pdf.setFontSize(sCourse); }
      const baseY = cy(346);
      const gap = px(26);
      const startY = baseY - Math.round(gap * (courseLines.length - 1) / 2);
      courseLines.forEach((ln: string, i: number) => {
        pdf.text(ln, Math.round(w / 2), startY + i * gap, { align: 'center' });
      });
      pdf.setFont('times', 'bold');
      pdf.setFontSize(px(16));
      pdf.setTextColor('#111827');
      pdf.text(formatDate(cert.issueDate), cx(360), cy(405));
      pdf.setFont('times', 'normal');
      pdf.setFontSize(px(10));
      pdf.setTextColor('#111827e9');
      pdf.text(verifyUrl, cx(348), cy(532));
      pdf.setFont('courier', 'normal');
      const idFontPx = (courseLines.length > 1 ? POS.pdf.idFontPxLong : POS.pdf.idFontPx);
      pdf.setFontSize(px(idFontPx));
      pdf.setTextColor('#6b7280');
      const idRight = (courseLines.length > 1 ? POS.pdf.idXRightLong : POS.pdf.idXRight);
      const idY = (courseLines.length > 1 ? POS.pdf.idYLong : POS.pdf.idY);
      pdf.text(cert.certificateId, w - cx(idRight), cy(idY), { align: 'right' });
      const qrPayload = buildQrPayload(cert);
      let qrData: string | null = null;
      try {
        qrData = await QRCode.toDataURL(qrPayload, {
          margin: 0,
          errorCorrectionLevel: 'H',
        });
      } catch (e) {
        console.warn('QR generation failed, skipping QR', e);
        qrData = null;
      }
      const qx = w - cx(450);
      const qy = cy(459);
      const qw = cx(60);
      const qh = cy(55);
      pdf.setDrawColor('#9ca3af');
      pdf.setLineWidth(1);
      pdf.rect(qx, qy, qw, qh);
      if (qrData) {
        try { pdf.addImage(qrData, 'PNG', qx, qy, qw, qh); } catch (e) { console.warn('Adding QR failed', e); }
      }
      pdf.save(`Certificate-${cert.certificateId}.pdf`);
    } catch (error) {
      console.error('Error generating PDF:', error);
      toast.error('Failed to generate PDF. Please try again.');
    } finally {
      setDownloading(false);
    }
  };

  if (loading) {
      return <div className="min-h-[60vh] flex items-center justify-center"><div className="animate-spin h-8 w-8 border-4 border-primary border-t-transparent rounded-full"></div></div>;
  }

  // Before any verification attempt, just show the input and button
  if (!attempted) {
      return (
          <div className="bg-gray-50 py-12">
              <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
                  <h1 className="text-3xl font-bold text-gray-900 mb-6">Verify Certificates Instantly</h1>
                  <p className="text-gray-600 mb-6">Enter a certificate ID to verify its authenticity.</p>
                  <div className="flex flex-col sm:flex-row gap-3 justify-center">
                      <input
                          value={inputCode}
                          onChange={(e) => setInputCode(e.target.value)}
                          placeholder="Enter Certificate ID"
                          className="bg-gray-50 border border-gray-200 text-gray-900 text-sm rounded-lg focus:ring-primary focus:border-primary block w-full sm:w-96 p-2.5 placeholder:text-xs placeholder:font-semibold"
                      />
                      <button
                          onClick={handleVerifyClick}
                          className="bg-primary text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-indigo-800 transition-colors"
                      >
                          Verify Now
                      </button>
                  </div>
              </div>
          </div>
      );
  }

  if (!cert) {
      return (
          <div className="min-h-[60vh] flex flex-col items-center justify-center text-center p-8">
              <XCircle className="w-16 h-16 text-red-500 mb-4" />
              <h2 className="text-2xl font-bold text-gray-900 mb-2">Certificate Not Found</h2>
              <p className="text-gray-500 mb-8">The certificate ID "{inputCode || id}" could not be verified in our records.</p>
              <button onClick={() => { setAttempted(false); setCert(null); }} className="px-6 py-2 bg-primary text-white rounded-md hover:bg-indigo-800">Verify Another</button>
          </div>
      );
  }

  return (
    <div className="bg-gray-50 py-12">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-8">Verify Certificates Instantly</h1>
        
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {/* Details Card */}
            <div className="bg-white rounded-xl p-8 shadow-sm h-fit">
                <div className="mb-6">
                    <label className="block text-sm font-medium text-gray-400 mb-2">Issued by: Speedlink Hi-Tech Solutions Limited</label>
                    <div className="flex gap-2">
                         <input 
                            value={inputCode || cert.certificateId}
                            onChange={(e) => setInputCode(e.target.value)}
                            placeholder="Enter Certificate ID"
                            className="bg-gray-50 border border-gray-200 text-gray-900 text-sm rounded-lg focus:ring-primary focus:border-primary block w-full p-2.5"
                         />
                         <button onClick={handleVerifyClick} className="bg-primary text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-indigo-800 transition-colors">Verify Now</button>
                    </div>
                </div>

                <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-6 flex items-center gap-2">
                    <ShieldCheck className="w-5 h-5 text-green-600" />
                    <span className="text-green-700 font-bold">Verified Certificate</span>
                </div>

                <div className="space-y-4">
                    <div className="border-b border-gray-100 pb-2">
                        <p className="text-xs text-gray-400 uppercase tracking-wider">Student Name</p>
                        <p className="text-lg font-medium text-gray-900">{cert.studentName}</p>
                    </div>
                    <div className="border-b border-gray-100 pb-2">
                        <p className="text-xs text-gray-400 uppercase tracking-wider">Course Name</p>
                        <p className="text-lg font-medium text-gray-900">{cert.courseName}</p>
                    </div>
                    <div className="border-b border-gray-100 pb-2">
                        <p className="text-xs text-gray-400 uppercase tracking-wider">Date Issued</p>
                        <p className="text-lg font-medium text-gray-900">{cert.issueDate}</p>
                    </div>
                    {cert.description && (
                        <div>
                           <p className="text-xs text-gray-400 uppercase tracking-wider">Description</p>
                           <p className="text-sm text-gray-600 mt-1">{cert.description}</p>
                        </div>
                    )}
                </div>
            </div>

            <div className="relative group">
                <div className="bg-white rounded-xl p-4 shadow-sm flex items-start justify-center relative" style={{ minHeight: '620px' }}>
                    <div className="scale-[0.75] origin-center">
                      <div
                        ref={certificateRef}
                        className="w-[842px] h-[595px] relative shadow-2xl bg-white"
                        style={{
                          backgroundImage: `url(${CERT_BG_URL})`,
                          backgroundSize: "100% 100%",
                          backgroundPosition: "center",
                          backgroundRepeat: "no-repeat"
                        }}
                      >
                        <div className="relative z-10 w-full h-full px-0 text-center">
                            <h2
                                className="font-serif font-bold text-gray-900 capitalize absolute left-1/2 -translate-x-1/2 leading-tight max-w-[72%] text-center whitespace-nowrap"
                                style={{ top: '234px', fontSize: '34px', fontFamily: "'Times New Roman', Times, serif" }}
                            >
                                {cert.studentName}
                            </h2>
                            <h3
                                className="font-medium leading-snug text-indigo-900 uppercase tracking-normal absolute left-1/2 -translate-x-1/2 -translate-y-1/2 max-w-[72%] text-center whitespace-normal break-words"
                                style={{ top: '340px', fontSize: getFitSize(cert.courseName, 20, 18, 20), fontFamily: "'Times New Roman', Times, serif" }}
                            >
                                {cert.courseName}
                            </h3>
                            <div className="absolute" style={{ top: '522px', left: '349px', fontSize: '10px' }}>
                                <p className="text-gray-800 italic">{verifyUrl}</p>
                            </div>
                            <div className="absolute" style={{ top: `${((cert.courseName || '').length > 26 ? POS.preview.idTopLong : POS.preview.idTop)}px`, right: `${((cert.courseName || '').length > 26 ? POS.preview.idRightLong : POS.preview.idRight)}px` }}>
                                <span className="font-mono text-gray-800" style={{ fontFamily: "'Courier New', monospace", lineHeight: '1', fontSize: '11px' }}>{cert.certificateId}</span>
                            </div>
                            <div className="absolute" style={{ top: '435px', right: '367px', width: '85px', height: '60px' }}>
                                <QRCodeCanvas
                                    value={buildQrPayload(cert)}
                                    size={65}
                                    level="H"
                                    includeMargin={false}
                                />
                            </div>
                        </div>
                      </div>
                    </div>
                </div>
                <div className="mt-4 flex justify-end">
                    <button
                        onClick={handleDownloadPDF}
                        disabled={downloading}
                        className="bg-primary text-white px-6 py-3 rounded-full font-medium flex items-center gap-2 hover:bg-indigo-800 transition-all shadow-lg disabled:opacity-50"
                    >
                        {downloading ? (
                            <div className="animate-spin h-5 w-5 border-2 border-white border-t-transparent rounded-full"></div>
                        ) : (
                            <Download className="w-5 h-5" />
                        )}
                        {downloading ? 'Generating...' : 'Download PDF'}
                    </button>
                </div>
            </div>
        </div>
      </div>
    </div>
  );
};
